import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider, useRouteError, Link } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./styles.css";
import { ApiError } from "./api";
import { AuthProvider, useIsOwner } from "./auth";
import { Layout } from "./layout";
import { ToastProvider, Alert, Empty } from "./ui";
import Dashboard from "./pages/Dashboard";
import Enquiries from "./pages/Enquiries";
import Bookings from "./pages/Bookings";
import Settings from "./pages/Settings";
import { CustomerDetail, CustomerList } from "./pages/Customers";
import { VehicleDetail, VehicleList } from "./pages/Vehicles";
import { JobDetail, JobList, JobNew } from "./pages/Jobs";
import { QuoteDetail, QuoteList, QuoteNew } from "./pages/Quotes";
import { InvoiceDetail, InvoiceList, InvoiceNew, PaymentList } from "./pages/Invoices";
import { BillDetail, BillNew, ExpenseList, StockList, SupplierDetail, SupplierList } from "./pages/Purchases";
import StaffPage from "./pages/Staff";
import BankPage from "./pages/Bank";
import Reports from "./pages/Reports";
import FinanceHub, { LedgerPage } from "./pages/Finance";
import Followups from "./pages/Followups";
import PublicLink from "./pages/PublicLink";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2, refetchOnWindowFocus: true, staleTime: 15_000 },
    mutations: { retry: false },
  },
});

function RouteError() {
  const e = useRouteError() as Error;
  return <div className="content"><Alert kind="error" title="This page failed to load">{e?.message ?? "Unknown error"} — <a href="/">return to dashboard</a></Alert></div>;
}
function NotFound() {
  return <Empty title="Page not found" action={<Link to="/" className="btn btn-primary">Go to dashboard</Link>} />;
}
function OwnerOnly({ children }: { children: React.ReactNode }) {
  return useIsOwner() ? <>{children}</> : <Alert kind="warn" title="Owner access only">Ask the owner if you need this information.</Alert>;
}

const router = createBrowserRouter([
  {
    path: "/", element: <Layout />, errorElement: <RouteError />,
    children: [
      { index: true, element: <Dashboard /> },
      { path: "enquiries", element: <Enquiries /> },
      { path: "bookings", element: <Bookings /> },
      { path: "jobs", element: <JobList /> },
      { path: "jobs/new", element: <JobNew /> },
      { path: "jobs/:id", element: <JobDetail /> },
      { path: "followups", element: <Followups /> },
      { path: "quotes", element: <QuoteList /> },
      { path: "quotes/new", element: <QuoteNew /> },
      { path: "quotes/:id", element: <QuoteDetail /> },
      { path: "customers", element: <CustomerList /> },
      { path: "customers/:id", element: <CustomerDetail /> },
      { path: "vehicles", element: <VehicleList /> },
      { path: "vehicles/:id", element: <VehicleDetail /> },
      { path: "invoices", element: <InvoiceList /> },
      { path: "invoices/new", element: <InvoiceNew /> },
      { path: "invoices/:id", element: <InvoiceDetail /> },
      { path: "payments", element: <OwnerOnly><PaymentList /></OwnerOnly> },
      { path: "expenses", element: <OwnerOnly><ExpenseList /></OwnerOnly> },
      { path: "suppliers", element: <OwnerOnly><SupplierList /></OwnerOnly> },
      { path: "suppliers/:id", element: <OwnerOnly><SupplierDetail /></OwnerOnly> },
      { path: "bills/new", element: <OwnerOnly><BillNew /></OwnerOnly> },
      { path: "bills/:id", element: <OwnerOnly><BillDetail /></OwnerOnly> },
      { path: "stock", element: <OwnerOnly><StockList /></OwnerOnly> },
      { path: "staff", element: <OwnerOnly><StaffPage /></OwnerOnly> },
      { path: "bank", element: <OwnerOnly><BankPage /></OwnerOnly> },
      { path: "reports", element: <OwnerOnly><Reports /></OwnerOnly> },
      { path: "reports/:tab", element: <OwnerOnly><Reports /></OwnerOnly> },
      { path: "finance", element: <OwnerOnly><FinanceHub /></OwnerOnly> },
      { path: "finance/ledger", element: <OwnerOnly><LedgerPage /></OwnerOnly> },
      { path: "settings", element: <Settings /> },
      { path: "*", element: <NotFound /> },
    ],
  },
]);

// Customer links (/l/<token>) are public pages: rendered without the staff sign-in.
const publicToken = /^\/l\/([A-Za-z0-9_-]{20,64})\/?$/.exec(window.location.pathname)?.[1];

createRoot(document.getElementById("root")!).render(publicToken ? <StrictMode><ToastProvider><PublicLink token={publicToken} /></ToastProvider></StrictMode> :
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
